import { Button } from "@shared-ui/button";
import { useMutation } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/dialog";
import {
	type AdminCategoryRow,
	deleteAdminCategoryTemplate,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Confirm removing a category's specification template.
 *
 * Its own component for the reason `CategoryDialog` is: this is not a yes/no
 * box, it is the only place the consequences of an irreversible action are
 * stated, and burying that inside a 400-line page is how the wording rots.
 *
 * The mutation lives HERE, and the page renders this component with a `key` of
 * the category id. Both are load-bearing and neither is sufficient alone: owning
 * the mutation stops it outliving the dialog, and the key stops it outliving a
 * RETARGET, where `category` changes without an unmount and the instance would
 * otherwise keep the previous category's error banner.
 */
interface RemoveTemplateDialogProps {
	category: AdminCategoryRow;
	onClose: () => void;
	onRemoved: () => Promise<void> | void;
}

const TITLE_ID = "remove-template-dialog-title";

export function RemoveTemplateDialog({
	category,
	onClose,
	onRemoved,
}: RemoveTemplateDialogProps) {
	const remove = useMutation({
		mutationFn: () => deleteAdminCategoryTemplate(category.id),
		onSuccess: async () => {
			await onRemoved();
			onClose();
		},
	});

	return (
		// The shared shell, like `merge-brand-dialog` and `category-dialog`. This
		// one destroys a template and strands the specifications keyed to it, so
		// having Escape and a focus trap rather than a bare overlay matters.
		<Dialog
			open
			titleId={TITLE_ID}
			// Same rule again. This one destroys a template and strands the
			// specifications keyed to it, so a mid-flight dismissal is the worst
			// of the three to allow.
			onClose={() => {
				if (!remove.isPending) onClose();
			}}
			className="grid w-full max-w-lg gap-4 rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Remove the template on {category.name}?
				</h2>
				<p className="text-sm text-muted-foreground">
					This is the step that lets the category take children.
				</p>
			</div>

			{/*
			 * TWO COSTS, stated conditionally because both are.
			 *
			 * The publish lapse lasts as long as this category holds no template.
			 * It can hold one again only while it has no children, so the way out
			 * is to re-file these products onto the new subcategories -- which is
			 * the only remedy this app can actually perform, since nothing here
			 * re-parents a category.
			 *
			 * And what customers see changes, which is easy to miss: the public
			 * product page reads each spec row's unit and ordering from the
			 * template, so live pages lose both immediately.
			 *
			 * That third sentence was FALSE when it was written and is true
			 * now. `getCatalogProduct` dropped `specTemplate` on the way in,
			 * so the page had never had units or ordering to lose and this
			 * dialog warned about a cost that did not exist. Found while
			 * tracing the same unwrap in the admin client; fixed there rather
			 * than softened here, because the sentence describes what the
			 * screen is supposed to do.
			 */}
			<div className="grid gap-2 rounded-2xl border bg-amber-50/70 p-3 text-sm text-amber-900">
				<p>
					The attribute definitions are deleted. They are recorded in the audit
					log, but this screen cannot put them back.
				</p>
				<p>
					{category.productCount === 1
						? "1 product sits here."
						: `${category.productCount} products sit here.`}{" "}
					While this category has no template, publishing them will not check
					for required specifications. It can only carry one again once it has
					no subcategories -- so re-file these products under the new
					subcategories.
				</p>
				{category.productCount > 0 ? (
					<p>
						Products already published here keep their specifications, but the
						public page will show them without units and in a different order
						until they are re-filed.
					</p>
				) : null}
			</div>

			{remove.isError ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(remove.error, "Could not remove the template.")}
				</p>
			) : null}

			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
					Cancel
				</Button>
				<Button onClick={() => remove.mutate()} disabled={remove.isPending}>
					{remove.isPending ? "Removing..." : "Remove template"}
				</Button>
			</div>
		</Dialog>
	);
}
